import React from "react";
import { Box, Typography, keyframes, Icon, Divider } from "@mui/material";
import { useTheme } from "@emotion/react";

// Animation for the "Last updated" text appearing
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for emphasis
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const TransparentUpdatesIcon = ({ variant = "light" }) => {
  const theme = useTheme();
  const { primary, overlay } = theme.palette;
  const isDark = variant === "dark";

  // Colors based on variant
  const placeholderColor = isDark ? overlay.white30 : "rgba(255, 255, 255, 0.3)";
  const iconColor = isDark ? overlay.white50 : "rgba(255, 255, 255, 0.6)";
  const timestampBg = isDark ? overlay.white20 : "rgba(255, 255, 255, 0.2)";
  const timestampText = isDark ? primary.contrastText : "rgba(255, 255, 255, 0.9)";
  const edgeLineColor = "rgba(255, 255, 255, 0.3)";
  const dividerColor = "rgba(255, 255, 255, 0.1)";
  const backgroundColor = isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.4)";

  return (
    <Box
      sx={{
        position: "relative",
        width: "200px",
        height: "140px",
        margin: "0 auto",
        backgroundColor: backgroundColor,
        backdropFilter: "blur(10px)",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        padding: "16px",
      }}
    >
      {/* Left edge line */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />
      
      {/* Right edge line */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />

      {/* Calendar Header Section */}
      <Box
        sx={{
          width: "100%",
          mb: 2,
          mt: 1,
        }}
      >
        {/* Calendar Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {/* Calendar icon */}
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontSize: "16px",
              color: iconColor,
              opacity: 0.8,
              fontWeight: 300,
            }}
          >
            calendar_month
          </Icon>
          {/* Title placeholder */}
          <Box
            sx={{
              flex: 1,
              height: "10px",
              backgroundColor: placeholderColor,
              borderRadius: "5px",
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ borderColor: dividerColor, mb: 2 }} />

      {/* Event Details Section */}
      <Box sx={{ flex: 1 }}>
        {/* Location Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "12px",
          }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontSize: "16px",
              color: iconColor,
              opacity: 0.8,
              fontWeight: 300,
              minWidth: "16px",
            }}
          >
            location_on
          </Icon>
          <Box
            sx={{
              flex: 1,
              height: "8px",
              backgroundColor: placeholderColor,
              borderRadius: "4px",
            }}
          />
        </Box>

        {/* Person/Creator Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "12px",
          }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontSize: "16px",
              color: iconColor,
              opacity: 0.8,
              fontWeight: 300,
              minWidth: "16px",
            }}
          >
            person
          </Icon>
          <Box
            sx={{
              flex: 1,
              height: "8px",
              backgroundColor: placeholderColor,
              borderRadius: "4px",
              width: "80%",
            }}
          />
        </Box>

        {/* Description Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            mb: "12px",
          }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontSize: "16px",
              color: iconColor,
              opacity: 0.8,
              fontWeight: 300,
              minWidth: "16px",
              mt: "-2px"
            }}
          >
            notes
          </Icon>
          <Box sx={{ flex: 1 }}>
            {/* Single description line */}
            <Box
              sx={{
                height: "8px",
                backgroundColor: placeholderColor,
                borderRadius: "4px",
                mb: "6px",
                width: "100%",
              }}
            />
            
            {/* "Last updated" timestamp - aligned with description */}
            <Box
              sx={{
                backgroundColor: timestampBg,
                borderRadius: "6px",
                padding: "6px 8px",
                animation: `${fadeInUp} 1s ease-out 0.5s both, ${pulse} 2s ease-in-out 1.5s infinite`,
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: timestampText,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                }}
              >
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 300,
                  }}
                >
                  schedule
                </Icon>
                Last updated: 2 hrs ago
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TransparentUpdatesIcon;